import { useEffect, useState, useMemo } from "react";
import {
	Check,
	ChevronDown,
	ChevronUp,
	Clock,
	Edit2,
	LineChart as LineChartIcon,
	Loader2,
	Trash2,
	X,
} from "lucide-react";
import { cn, fetchAPI } from "../lib/utils";
import { formatValueByUnit } from "../lib/coachUtils";
import { buildExerciseHistoryChartSeries } from "../lib/exerciseHistoryUtils";
import { ExerciseHistoryChart } from "./ExerciseHistoryChart";
import { ShootingResultBlock } from "./shooting/ShootingResultBlock";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

/**
 * Formats a result entry for display based on exercise type
 */
function formatResultDisplay(result, exercise, t) {
	if (!result || !exercise) return "-";

	const exType = exercise.exercise_type || exercise.type || "";

	// Strength exercise
	if (exType === "strength") {
		const parts = [];
		const pushUniquePart = (value) => {
			if (value === null || value === undefined) return;
			const normalized = String(value).trim();
			if (!normalized) return;
			if (parts.some((existing) => String(existing).trim() === normalized))
				return;
			parts.push(normalized);
		};
		const weight =
			result.weight_kg ?? result.raw_weight ?? result.value ?? result.score;
		if (weight !== null && weight !== undefined && weight !== "") {
			pushUniquePart(`${weight} kg`);
		}
		if (result.reps) pushUniquePart(`${result.reps} ${t("repsShort")}`);
		if (result.raw_reps) pushUniquePart(`${result.raw_reps} ${t("repsShort")}`);
		if (result.rpe) pushUniquePart(`RPE ${result.rpe}`);
		if (parts.length > 0) return parts.join(" × ");

		if (typeof result.detail === "string" && result.detail.trim()) {
			const cleanDetail = result.detail
				.replace(/\s*\(da[^)]*Epley[^)]*\)/gi, "")
				.replace(/,\s*Epley/gi, "")
				.replace(/\(Epley\)/gi, "")
				.replace(/\s{2,}/g, " ")
				.trim();
			if (cleanDetail) return cleanDetail;
		}

		return "-";
	}

	// Tiro. **Mancava proprio il ramo**: `formatResultDisplay` ramificava su
	// strength / running / swim / bike / erg / conditioning e un risultato di
	// tiro cadeva sul `return "-"` finale. Il dato era salvato e corretto, non
	// veniva letto — ed è il motivo per cui lo storico dei punteggi si vedeva
	// vuoto.
	if (exType === "dry_fire" || exType === "live_fire") {
		const shooting = result.shooting || {};
		if (shooting.prescription_format === "free") {
			return shooting.self_rating ? `${shooting.self_rating}/5` : "-";
		}
		const parts = [];
		if (exType === "dry_fire" || shooting.mode === "dry_fire") {
			if (shooting.best_time_seconds != null)
				parts.push(`${Number(shooting.best_time_seconds).toFixed(2)}s`);
			if (shooting.avg_time_seconds != null)
				parts.push(`media ${Number(shooting.avg_time_seconds).toFixed(2)}s`);
			return parts.length ? parts.join(" · ") : "-";
		}
		// La metrica di ranking è congelata sul risultato: si legge quella,
		// o un sistema time-plus mostrerebbe un hit factor che non significa
		// niente. Il numero è lo stesso della classifica (`best_score`, la
		// serie migliore): il tempo finale col time-plus, non quello grezzo.
		const metric = shooting.metric || "hit_factor";
		const best = shooting.best_score;
		if (metric === "hit_factor" && shooting.hit_factor != null) {
			parts.push(`HF ${Number(shooting.hit_factor).toFixed(2)}`);
		} else if (
			metric === "time_plus" &&
			(best ?? shooting.total_time_seconds) != null
		) {
			parts.push(`${Number(best ?? shooting.total_time_seconds).toFixed(2)}s`);
		} else if ((best ?? shooting.total_points) != null) {
			parts.push(`${best ?? shooting.total_points} punti`);
		}
		// Le serie non si sommano in un numero solo: dire su quante prove è
		// quel valore è ciò che lo rende confrontabile.
		if (shooting.series_count > 1) {
			parts.push(
				shooting.series_scoring === "combined"
					? `${shooting.series_count} serie sommate`
					: `migliore di ${shooting.series_count} serie`,
			);
		}
		// La distanza, quando la serie migliore ce l'ha: è il record per
		// distanza del lento mirato.
		const bestSeries = (shooting.series || shooting.strings || [])[
			shooting.best_series_index ?? 0
		];
		if (bestSeries?.distance_m) parts.push(`@${bestSeries.distance_m}m`);
		// Con quale arma: due prove in division diverse non si confrontano.
		if (shooting.division)
			parts.push(String(shooting.division).replace(/_/g, " "));
		return parts.length ? parts.join(" · ") : "-";
	}

	if (typeof result.detail === "string" && result.detail.trim()) {
		return result.detail.trim();
	}

	// Running
	if (exType === "running") {
		if (result.time_seconds) {
			const mins = Math.floor(result.time_seconds / 60);
			const secs = result.time_seconds % 60;
			const formatted = `${mins}:${String(secs).padStart(2, "0")}`;
			if (result.distance_meters) {
				const km = result.distance_meters / 1000;
				return `${formatted} @ ${km.toFixed(1)}km`;
			}
			return formatted;
		}
		return "-";
	}

	// Swimming
	if (exType === "swim") {
		if (result.time_seconds) {
			const mins = Math.floor(result.time_seconds / 60);
			const secs = result.time_seconds % 60;
			return `${mins}:${String(secs).padStart(2, "0")}`;
		}
		return "-";
	}

	// Bike/Erg
	if (["bike", "erg"].includes(exType)) {
		const parts = [];
		if (result.distance_meters) {
			const km = result.distance_meters / 1000;
			parts.push(`${km.toFixed(1)}km`);
		}
		if (result.time_seconds) {
			const mins = Math.floor(result.time_seconds / 60);
			const secs = result.time_seconds % 60;
			parts.push(`${mins}:${String(secs).padStart(2, "0")}`);
		}
		if (result.avg_power) parts.push(`${result.avg_power}W`);
		return parts.length > 0 ? parts.join(" · ") : "-";
	}

	// Conditioning
	if (exType === "conditioning") {
		const condType = exercise.conditioning_type || "";
		const parts = [];

		// Schema-driven results
		if (
			Array.isArray(result.schema_values) &&
			result.schema_values.length > 0
		) {
			result.schema_values.forEach((sv) => {
				if (sv.value !== null && sv.value !== undefined && sv.value !== "") {
					const formatted = formatValueByUnit(sv.value, sv.unit);
					parts.push(formatted);
				}
			});
		}

		// Legacy free score
		if (
			(!Array.isArray(result.schema_values) ||
				result.schema_values.length === 0) &&
			((result.score !== null && result.score !== undefined) ||
				(result.value !== null && result.value !== undefined))
		) {
			const numericScore =
				result.score !== null && result.score !== undefined
					? result.score
					: result.value;
			if (Number.isInteger(Number(numericScore))) {
				if (["amrap", "for_time"].includes(condType)) {
					parts.push(`${numericScore} rounds`);
				} else {
					parts.push(String(numericScore));
				}
			} else {
				parts.push(String(numericScore));
			}
		}

		// Reps text (AMRAP/conditioning)
		if (result.reps_text) {
			parts.push(result.reps_text);
		}

		return parts.length > 0 ? parts.join(" · ") : "-";
	}

	return "-";
}

export function ExerciseHistoryDialog({
	open,
	onOpenChange,
	exercise,
	loading,
	error,
	results = [],
	onHistoryChanged,
	athleteId = null,
	t,
}) {
	const [scrolled, setScrolled] = useState(false);
	const [chartOpen, setChartOpen] = useState(true);
	const [expandedResultIds, setExpandedResultIds] = useState({});
	const [editingResultId, setEditingResultId] = useState(null);
	const [savingResultId, setSavingResultId] = useState(null);
	const [editDraft, setEditDraft] = useState({
		date: "",
		value: "",
		weight: "",
		reps: "",
		notes: "",
	});

	// Sort results by date descending (most recent first)
	const sortedResults = useMemo(() => {
		return [...(Array.isArray(results) ? results : [])].sort((a, b) => {
			const dateA = new Date(a.created_at || a.date || 0);
			const dateB = new Date(b.created_at || b.date || 0);
			return dateB - dateA;
		});
	}, [results]);

	const chartSeries = useMemo(
		() => buildExerciseHistoryChartSeries(sortedResults, exercise),
		[sortedResults, exercise],
	);

	useEffect(() => {
		if (!open) {
			setEditingResultId(null);
			setSavingResultId(null);
			setEditDraft({
				date: "",
				value: "",
				weight: "",
				reps: "",
				notes: "",
			});
		}
	}, [open]);

	const handleScroll = (e) => {
		setScrolled(e.target.scrollLeft > 0 || e.target.scrollTop > 0);
	};

	const formatDateDisplay = (dateStr) => {
		if (!dateStr) return "";
		try {
			const d = new Date(dateStr);
			if (isNaN(d.getTime())) return "";
			const locale = document.documentElement.lang === "it" ? "it-IT" : "en-GB";
			return d.toLocaleDateString(locale, {
				weekday: "short",
				day: "numeric",
				month: "short",
				year: "numeric",
			});
		} catch {
			return "";
		}
	};

	const exerciseName =
		exercise?.exercise_name ||
		exercise?.name ||
		exercise?.exercise_id ||
		"Exercise";

	const toggleExpanded = (resultId) => {
		if (!resultId) return;
		setExpandedResultIds((prev) => ({
			...prev,
			[resultId]: !prev[resultId],
		}));
	};

	const getHistoryItemKey = (result) => {
		if (!result) return "";
		if (result?.result_id) {
			return `${result.result_id}:${result?.superset_detail_index ?? "direct"}`;
		}
		return `${result?.entry_type || "item"}:${result?.date || ""}:${result?.superset_detail_index ?? "na"}`;
	};

	const isStrengthEntry = (result) => {
		const exType = exercise?.exercise_type || exercise?.type || "";
		return (
			exType === "strength" ||
			result?.unit === "kg" ||
			result?.raw_weight !== undefined ||
			result?.weight_kg !== undefined
		);
	};

	const isTimedEntry = (result) => {
		const exType = exercise?.exercise_type || exercise?.type || "";
		// Il tiro decide da sé: un risultato di tiro ha `time_seconds`
		// denormalizzato, quindi senza questo controllo verrebbe letto come
		// "a tempo" e mostrerebbe i secondi al posto dell'hit factor — che è
		// la metrica su cui quel drill si giudica.
		if (result?.shooting) {
			return result.shooting.direction === "lower";
		}
		return (
			result?.unit === "seconds" ||
			result?.unit === "minutes_seconds" ||
			result?.time_seconds !== undefined ||
			["running", "swim", "bike", "erg"].includes(exType)
		);
	};

	const parseNumericInput = (value) => {
		if (value === null || value === undefined) return null;
		const normalized = String(value).trim().replace(/,/g, ".");
		if (!normalized) return null;
		const parsed = Number(normalized);
		return Number.isFinite(parsed) ? parsed : null;
	};

	const handleStartEdit = (result) => {
		if (!result?.result_id || !result?.can_edit) return;
		setEditingResultId(getHistoryItemKey(result));
		setEditDraft({
			date: result.date || "",
			value:
				result?.value !== null && result?.value !== undefined
					? String(result.value)
					: result?.score !== null && result?.score !== undefined
						? String(result.score)
						: result?.time_seconds !== null &&
							  result?.time_seconds !== undefined
							? String(result.time_seconds)
							: result?.reps !== null && result?.reps !== undefined
								? String(result.reps)
								: "",
			weight:
				result?.raw_weight !== null && result?.raw_weight !== undefined
					? String(result.raw_weight)
					: result?.weight_kg !== null && result?.weight_kg !== undefined
						? String(result.weight_kg)
						: "",
			reps:
				result?.raw_reps !== null && result?.raw_reps !== undefined
					? String(result.raw_reps)
					: result?.reps !== null && result?.reps !== undefined
						? String(result.reps)
						: "",
			notes: result?.notes || "",
		});
	};

	const handleCancelEdit = () => {
		setEditingResultId(null);
		setEditDraft({
			date: "",
			value: "",
			weight: "",
			reps: "",
			notes: "",
		});
	};

	// In anteprima coach lo storico e' quello dell'ATLETA: le correzioni devono
	// passare dalle rotte /coach/athletes/{id}/..., altrimenti il coach
	// correggerebbe una riga del proprio diario (o non la troverebbe affatto).
	const diaryEndpoint = (resultId) =>
		athleteId
			? `/coach/athletes/${encodeURIComponent(athleteId)}/performance-profile/history/diary/${encodeURIComponent(resultId)}`
			: `/profile/performance-profile/history/diary/${encodeURIComponent(resultId)}`;

	const handleSaveEdit = async (result) => {
		if (!result?.result_id || !result?.can_edit) return;
		const resultKey = getHistoryItemKey(result);

		const payload = {
			superset_detail_index: Number.isInteger(result?.superset_detail_index)
				? result.superset_detail_index
				: undefined,
			date: editDraft.date || undefined,
			notes: editDraft.notes || "",
		};

		if (isStrengthEntry(result)) {
			payload.weight_kg = parseNumericInput(editDraft.weight);
			payload.reps = parseNumericInput(editDraft.reps);
		} else if (isTimedEntry(result)) {
			payload.time_seconds = parseNumericInput(editDraft.value);
		} else if (result?.unit === "reps") {
			payload.reps = parseNumericInput(editDraft.value);
		} else {
			payload.score = parseNumericInput(editDraft.value);
		}

		setSavingResultId(resultKey);
		try {
			await fetchAPI(diaryEndpoint(result.result_id), {
				method: "PUT",
				body: JSON.stringify(payload),
			});
			await onHistoryChanged?.();
			handleCancelEdit();
			toast.success(t("historyEntryUpdated") || "Risultato storico aggiornato");
		} catch (saveError) {
			toast.error(saveError?.message || t("error") || "Errore");
		} finally {
			setSavingResultId(null);
		}
	};

	const handleDelete = async (result) => {
		if (!result?.result_id || !result?.can_delete) return;
		const resultKey = getHistoryItemKey(result);
		const confirmed = window.confirm(
			t("historyDeleteConfirm") || "Eliminare questo risultato dallo storico?",
		);
		if (!confirmed) return;

		setSavingResultId(resultKey);
		try {
			const query = Number.isInteger(result?.superset_detail_index)
				? `?superset_detail_index=${result.superset_detail_index}`
				: "";
			await fetchAPI(`${diaryEndpoint(result.result_id)}${query}`, {
				method: "DELETE",
			});
			await onHistoryChanged?.();
			if (editingResultId === resultKey) {
				handleCancelEdit();
			}
			toast.success(t("historyEntryDeleted") || "Risultato storico eliminato");
		} catch (deleteError) {
			toast.error(deleteError?.message || t("error") || "Errore");
		} finally {
			setSavingResultId(null);
		}
	};

	const renderSeriesDetails = (result) => {
		const pushUniquePart = (parts, value) => {
			if (value === null || value === undefined) return;
			const normalized = String(value).trim();
			if (!normalized) return;
			if (parts.some((existing) => String(existing).trim() === normalized))
				return;
			parts.push(normalized);
		};

		const rows = Array.isArray(result?.result_set_rows)
			? result.result_set_rows.filter(Boolean)
			: [];
		const supersetRows = Array.isArray(result?.superset_details)
			? result.superset_details.filter(Boolean)
			: [];

		if (rows.length === 0 && supersetRows.length === 0) return null;

		return (
			<div className="mt-2 space-y-2 border-t border-border/60 pt-2">
				{rows.length > 0 && (
					<div className="space-y-1">
						<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
							Serie
						</p>
						<div className="space-y-1">
							{rows.map((row, index) => {
								const rowIndex = Number.isFinite(Number(row?.row_index))
									? Number(row.row_index) + 1
									: index + 1;
								const weight = row?.weight_kg;
								const reps = row?.reps;
								const repsText = row?.reps_text;
								const rpe = row?.rpe;
								const parts = [];
								if (weight !== null && weight !== undefined && weight !== "") {
									pushUniquePart(parts, `${weight} kg`);
								}
								if (reps !== null && reps !== undefined && reps !== "") {
									pushUniquePart(parts, `${reps} ${t("repsShort")}`);
								}
								if (repsText) pushUniquePart(parts, repsText);
								if (rpe !== null && rpe !== undefined && rpe !== "") {
									pushUniquePart(parts, `RPE ${rpe}`);
								}
								return (
									<div
										key={`row-${rowIndex}`}
										className="flex items-center justify-between gap-2 rounded-sm border border-border/60 bg-background/30 px-2 py-1.5"
									>
										<p className="text-[11px] text-muted-foreground shrink-0">
											{rowIndex}.
										</p>
										<p className="text-[11px] text-foreground text-right flex-1 truncate">
											{parts.length > 0 ? parts.join(" · ") : "-"}
										</p>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{supersetRows.length > 0 && (
					<div className="space-y-1">
						<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
							Dettagli superset
						</p>
						<div className="space-y-1">
							{supersetRows.map((detail, index) => {
								const label =
									detail?.exercise_name || String.fromCharCode(65 + index);
								const weight = detail?.weight_kg;
								const reps = detail?.reps;
								const rpe = detail?.rpe;
								const parts = [];
								if (weight !== null && weight !== undefined && weight !== "") {
									pushUniquePart(parts, `${weight} kg`);
								}
								if (reps !== null && reps !== undefined && reps !== "") {
									pushUniquePart(parts, `${reps} ${t("repsShort")}`);
								}
								if (rpe !== null && rpe !== undefined && rpe !== "") {
									pushUniquePart(parts, `RPE ${rpe}`);
								}
								return (
									<div
										key={`superset-${index}`}
										className="flex items-center justify-between gap-2 rounded-sm border border-border/60 bg-background/30 px-2 py-1.5"
									>
										<p className="text-[11px] text-muted-foreground shrink-0">
											{label}
										</p>
										<p className="text-[11px] text-foreground text-right flex-1 truncate">
											{parts.length > 0 ? parts.join(" · ") : "-"}
										</p>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderEditFields = (result) => {
		if (isStrengthEntry(result)) {
			return (
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
					<Input
						type="number"
						step="0.1"
						inputMode="decimal"
						value={editDraft.weight}
						onChange={(e) =>
							setEditDraft((prev) => ({ ...prev, weight: e.target.value }))
						}
						placeholder={t("weightKgLabel") || "Peso (kg)"}
						className="bg-card border-border rounded-sm h-10"
					/>
					<Input
						type="number"
						step="1"
						inputMode="numeric"
						value={editDraft.reps}
						onChange={(e) =>
							setEditDraft((prev) => ({ ...prev, reps: e.target.value }))
						}
						placeholder={t("repsShort") || "Reps"}
						className="bg-card border-border rounded-sm h-10"
					/>
				</div>
			);
		}

		return (
			<Input
				type="number"
				step="0.1"
				inputMode="decimal"
				value={editDraft.value}
				onChange={(e) =>
					setEditDraft((prev) => ({ ...prev, value: e.target.value }))
				}
				placeholder={
					isTimedEntry(result)
						? t("durationSeconds") || "Secondi"
						: t("value") || "Valore"
				}
				className="bg-card border-border rounded-sm h-10"
			/>
		);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex flex-col bg-card border-border w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] max-w-2xl p-3 sm:p-6 max-h-[92svh] overflow-hidden rounded-sm">
				<DialogDescription className="hidden" />
				<DialogHeader className="space-y-1.5 pr-7">
					<DialogTitle className="font-heading text-base truncate">
						{t("exerciseHistoryLabel") || "Storico Esercizio"}
					</DialogTitle>
					<p className="text-xs text-muted-foreground break-words">
						{exercise?.exercise_name || exercise?.name || ""}
					</p>
				</DialogHeader>

				{loading && (
					<div className="flex items-center justify-center flex-1">
						<Loader2 className="w-5 h-5 animate-spin text-volt" />
					</div>
				)}

				{error && !loading && (
					<div className="flex items-center justify-center flex-1 px-4 text-center">
						<p className="text-sm text-muted-foreground">{error}</p>
					</div>
				)}

				{!loading && !error && sortedResults.length === 0 && (
					<div className="flex flex-col items-center justify-center flex-1 px-4 text-center">
						<Clock className="w-8 h-8 text-muted-foreground mb-2" />
						<p className="text-sm font-medium text-muted-foreground">
							{t("noResultsFound") || "Nessun risultato trovato"}
						</p>
						<p className="text-xs text-muted-foreground mt-1">
							{t("startLoggingResults") ||
								"Inizia a registrare risultati per questo esercizio"}
						</p>
					</div>
				)}

				{!loading && !error && sortedResults.length > 0 && (
					<div
						className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
						onScroll={handleScroll}
					>
						<div className="space-y-1 pr-1">
							<div className="mb-2 flex items-start justify-between gap-2">
								<div className="min-w-0">
									<p className="text-xs font-medium text-volt uppercase tracking-wider">
										{exerciseName}
									</p>
									<p className="text-[10px] text-muted-foreground mt-0.5">
										{sortedResults.length} {t("results") || "risultati"}
									</p>
								</div>
								{chartSeries && (
									<Button
										variant="ghost"
										size="sm"
										className="h-7 px-2 shrink-0 text-[10px] text-muted-foreground hover:text-volt rounded-sm"
										onClick={() => setChartOpen((open) => !open)}
										title={t("exerciseProgressLabel") || "Progressione"}
									>
										<LineChartIcon className="w-3.5 h-3.5 mr-1" />
										{chartOpen
											? t("hide") || "Nascondi"
											: t("exerciseProgressLabel") || "Progressione"}
									</Button>
								)}
							</div>

							{chartSeries && chartOpen && (
								<div className="mb-3">
									<ExerciseHistoryChart series={chartSeries} t={t} />
								</div>
							)}

							{sortedResults.map((result, idx) => {
								const isShootingEntry =
									!!result?.shooting && typeof result.shooting === "object";
								// Un risultato di tiro porta la data della giornata
								// sparata (D33): è quella che il tiratore riconosce.
								const resultDate = isShootingEntry
									? formatDateDisplay(result.date) ||
										formatDateDisplay(result.created_at)
									: formatDateDisplay(result.created_at);
								const performance = formatResultDisplay(result, exercise, t);
								const resultId = result?.result_id || `${resultDate}-${idx}`;
								const resultKey = getHistoryItemKey(result);
								const isEditing = editingResultId === resultKey;
								const canShowDetails =
									(Array.isArray(result?.result_set_rows) &&
										result.result_set_rows.length > 0) ||
									(Array.isArray(result?.superset_details) &&
										result.superset_details.length > 0) ||
									// Tiro: le serie, bersaglio per bersaglio.
									(isShootingEntry &&
										(result.shooting.series || result.shooting.times || [])
											.length > 0);
								const isExpanded = Boolean(expandedResultIds[resultId]);

								return (
									<div
										key={`${resultId}-${result?.superset_detail_index ?? idx}`}
										className={cn(
											"rounded-sm border p-3 space-y-1.5 transition-colors",
											idx === 0
												? "border-volt/40 bg-volt/5 ring-1 ring-volt/20"
												: "border-border hover:border-border/80 bg-background/40",
										)}
									>
										{isEditing ? (
											<div className="space-y-2">
												<div className="flex items-center justify-between gap-2">
													<p className="text-xs font-medium text-foreground truncate">
														{performance}
													</p>
													<p className="text-[10px] text-muted-foreground shrink-0 uppercase tracking-wider">
														{t("editResultTitle") || "Modifica Risultato"}
													</p>
												</div>
												<Input
													type="date"
													value={editDraft.date}
													onChange={(e) =>
														setEditDraft((prev) => ({
															...prev,
															date: e.target.value,
														}))
													}
													className="bg-card border-border rounded-sm h-10"
												/>
												{renderEditFields(result)}
												<Textarea
													value={editDraft.notes}
													onChange={(e) =>
														setEditDraft((prev) => ({
															...prev,
															notes: e.target.value,
														}))
													}
													placeholder={t("notes") || "Note"}
													className="bg-card border-border rounded-sm min-h-[84px]"
												/>
												<div className="flex gap-2">
													<Button
														variant="default"
														size="sm"
														className="flex-1 bg-volt hover:bg-volt/90 rounded-sm"
														disabled={savingResultId === resultKey}
														onClick={() => handleSaveEdit(result)}
													>
														{savingResultId === resultKey ? (
															<Loader2 className="w-3.5 h-3.5 animate-spin" />
														) : (
															<Check className="w-3.5 h-3.5" />
														)}
														<span>{t("saveChanges") || "Salva Modifiche"}</span>
													</Button>
													<Button
														variant="secondary"
														size="sm"
														disabled={savingResultId === resultKey}
														onClick={handleCancelEdit}
													>
														<X className="w-3.5 h-3.5" />
														<span>{t("cancel") || "Annulla"}</span>
													</Button>
												</div>
											</div>
										) : (
											<>
												<div className="flex items-start justify-between gap-2">
													<div className="min-w-0 flex-1">
														<div className="flex items-baseline justify-between gap-2">
															<p className="text-xs font-medium text-foreground truncate">
																{performance}
															</p>
															<p className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">
																{resultDate ||
																	result.date ||
																	result.created_at ||
																	""}
															</p>
														</div>

														{result.notes && (
															<p className="text-[10px] text-muted-foreground italic mt-1">
																{result.notes}
															</p>
														)}
													</div>

													{(result.can_edit || result.can_delete) && (
														<div className="flex items-center gap-1 shrink-0">
															{result.can_edit && (
																<Button
																	variant="ghost"
																	size="icon"
																	className="h-9 w-9 rounded-sm text-muted-foreground hover:text-volt"
																	onClick={() => handleStartEdit(result)}
																	title={
																		t("editResultTitle") || "Modifica Risultato"
																	}
																>
																	<Edit2 className="w-3.5 h-3.5" />
																</Button>
															)}
															{result.can_delete && (
																<Button
																	variant="ghost"
																	size="icon"
																	className="h-9 w-9 rounded-sm text-muted-foreground hover:text-blaze hover:bg-blaze/10"
																	disabled={savingResultId === result.result_id}
																	onClick={() => handleDelete(result)}
																	title={
																		t("deleteResultTitle") ||
																		"Elimina risultato"
																	}
																>
																	<Trash2 className="w-3.5 h-3.5" />
																</Button>
															)}
														</div>
													)}
												</div>
											</>
										)}

										{canShowDetails && (
											<button
												type="button"
												onClick={() => toggleExpanded(resultId)}
												className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-volt/90"
											>
												{isExpanded ? (
													<ChevronUp className="w-3 h-3" />
												) : (
													<ChevronDown className="w-3 h-3" />
												)}
												<span>
													{isExpanded ? "Nascondi dettagli" : "Mostra dettagli"}
												</span>
											</button>
										)}

										{isExpanded &&
											(isShootingEntry ? (
												<div className="mt-2 border-t border-border/60 pt-2">
													<ShootingResultBlock
														exercise={exercise}
														result={result}
													/>
												</div>
											) : (
												renderSeriesDetails(result)
											))}

										{idx === 0 && (
											<div className="text-[9px] text-volt/80 uppercase tracking-wider font-medium pt-1">
												{t("mostRecent") || "Più recente"}
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
